import rcsLogo from "../assets/RCS-Logo-Blue.png";

const API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? "https://track-trace-server-production.up.railway.app"
    : "http://localhost:5000";

const money = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const resolveSignatureUrl = (value) => {
  const url = String(value || "").trim();
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_BASE_URL.replace(/\/$/, "")}/${url.replace(/^\/+/, "")}`;
};

const normalizeParticulars = (particulars) => {
  if (Array.isArray(particulars)) return particulars;
  if (particulars && typeof particulars === "object") return [particulars];
  return [];
};

const formatRequestParticulars = (request) =>
  normalizeParticulars(request?.particulars)
    .map((particular) => particular.description || particular.name || "")
    .filter(Boolean)
    .join(", ");

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString();
};

const getShipmentConsignee = (request) =>
  request?.shipment_consignee || request?.receiver_name || "";

const getEffectiveTaxRate = (particular) => {
  const numericRate = Number(particular?.tax_rate || 0);
  if (numericRate > 0) return numericRate;
  return particular?.with_tax ? 12 : 0;
};

const getWithTaxBreakdown = (total, taxRate = 12) => {
  const grossTotal = Number(total || 0);
  const rate = Number(taxRate || 0);
  if (!rate) {
    return {
      netAmount: grossTotal,
      inputTaxAmount: 0,
    };
  }
  const netAmount = Number((grossTotal / (1 + rate / 100)).toFixed(2));
  const inputTaxAmount = Number((netAmount * (rate / 100)).toFixed(2));

  return {
    netAmount,
    inputTaxAmount,
  };
};

const getUsdConvertedAmount = (phpAmount, exchangeRate) => {
  const rate = Number(exchangeRate || 1);
  return rate > 0 ? Number(phpAmount || 0) / rate : Number(phpAmount || 0);
};

const getEffectiveEwtAmount = (particular, netAmount) => {
  const storedAmount = Number(particular?.ewt_amount || 0);
  if (storedAmount > 0) return storedAmount;
  const rate = Number(particular?.ewt_rate || 0);
  if (!particular?.ewt_type || rate <= 0) return 0;
  return Number(((Number(netAmount || 0) * rate) / 100).toFixed(2));
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const buildRequestDetailHtml = (requests = [], soa = {}) =>
  requests
    .map((request) => {
      const particulars = normalizeParticulars(request.particulars);
      const itemCurrency = String(request.currency || "PHP").toUpperCase();
      const itemExchangeRate = Number(request.exchange_rate || 1);
      const isUsdItem = itemCurrency === "USD" && itemExchangeRate > 0;
      const requestTotalPhp = Number(request.total_amount || 0);
      const requestTotalOriginal = isUsdItem ? requestTotalPhp / itemExchangeRate : requestTotalPhp;
      const requestRemarks = String(request.remarks || "").trim();
      const preparerSignatureUrl = resolveSignatureUrl(request?.preparer_signature_url);
      const approverSignatureUrl = resolveSignatureUrl(request?.approver_signature_url);
      const receiverSignatureUrl = resolveSignatureUrl(request?.receiver_signature_url);

      let requestTotalEwtPhp = 0;

      const particularsRowsHtml = particulars
        .map((particular) => {
          const totalOriginal = Number(
            particular.total || (particular.quantity || particular.qty || 1) * (particular.amount || 0)
          );
          const totalPhp = isUsdItem ? totalOriginal * itemExchangeRate : totalOriginal;
          const effectiveTaxRate = getEffectiveTaxRate(particular);
          const { netAmount, inputTaxAmount } = particular.with_tax
            ? getWithTaxBreakdown(totalPhp, effectiveTaxRate)
            : { netAmount: 0, inputTaxAmount: 0 };
          const netAmountUsd =
            isUsdItem && itemExchangeRate > 0 ? getUsdConvertedAmount(netAmount, itemExchangeRate) : 0;
          const inputTaxAmountUsd =
            isUsdItem && itemExchangeRate > 0 ? getUsdConvertedAmount(inputTaxAmount, itemExchangeRate) : 0;
          const ewtAmountPhp = getEffectiveEwtAmount(particular, particular.with_tax ? netAmount : totalPhp);
          const ewtAmountUsd =
            isUsdItem && itemExchangeRate > 0 ? getUsdConvertedAmount(ewtAmountPhp, itemExchangeRate) : 0;
          requestTotalEwtPhp += ewtAmountPhp;

          return `
            <tr>
              <td style="padding:6px 8px;border:1px solid #000;">
                <div>${escapeHtml(particular.description || particular.name || "—")}</div>
                <div>
                  <strong>Qty:</strong> ${escapeHtml(particular.quantity || particular.qty || 1)} &nbsp;
                  <strong>Unit Cost:</strong> ${isUsdItem ? "$" : "₱"}${money(particular.amount || 0)}
                </div>
                ${
                  isUsdItem
                    ? `<div style="font-size:11px;color:#555;margin-top:2px;"><strong>Currency:</strong> ${itemCurrency} <strong>Conversion Rate:</strong> ${itemExchangeRate.toFixed(
                        4
                      )}</div>`
                    : ""
                }
              </td>
              <td style="padding:6px 8px;border:1px solid #000;text-align:right;">
                ${
                  particular.with_tax
                    ? isUsdItem
                      ? `$${money(netAmountUsd)}<br><span style="font-size:11px;color:#555;">₱${money(netAmount)}</span>`
                      : `₱${money(netAmount)}`
                    : "—"
                }
              </td>
              <td style="padding:6px 8px;border:1px solid #000;text-align:right;">
                ${
                  particular.with_tax
                    ? isUsdItem
                      ? `$${money(inputTaxAmountUsd)}<br><span style="font-size:11px;color:#555;">₱${money(
                          inputTaxAmount
                        )}</span>`
                      : `₱${money(inputTaxAmount)}`
                    : "—"
                }
              </td>
              <td style="padding:6px 8px;border:1px solid #000;text-align:right;">
                ${
                  ewtAmountPhp > 0
                    ? isUsdItem
                      ? `$${money(ewtAmountUsd)}<br><span style="font-size:11px;color:#555;">₱${money(ewtAmountPhp)}</span>`
                      : `₱${money(ewtAmountPhp)}`
                    : "—"
                }
              </td>
              <td style="padding:6px 8px;border:1px solid #000;text-align:right;font-weight:600;">
                ${
                  isUsdItem
                    ? `$${money(totalOriginal)}<br><span style="font-size:11px;color:#555;">₱${money(totalPhp)}</span>`
                    : `₱${money(totalPhp)}`
                }
              </td>
            </tr>
          `;
        })
        .join("");

      const requestTotalEwtOriginal = isUsdItem ? requestTotalEwtPhp / itemExchangeRate : requestTotalEwtPhp;
      const requestNetPayablePhp = Number((requestTotalPhp - requestTotalEwtPhp).toFixed(2));
      const requestNetPayableOriginal = isUsdItem
        ? requestNetPayablePhp / itemExchangeRate
        : requestNetPayablePhp;

      return `
        <section class="request-page request-page-break">
          <h1 style="text-align:center;letter-spacing:2px;margin:0 0 4px;">CASH VOUCHER</h1>
          <div class="request-subhead">RFP No: ${escapeHtml(request.rfp_no || "—")}</div>
          <div style="margin-bottom:10px;font-size:13px;">
            <div><strong>Date:</strong> ${formatDate(request.created_at) || ""}</div>
            <div><strong>Payee:</strong> ${escapeHtml(request.payee || "")}</div>
            <div><strong>Job Order:</strong> ${escapeHtml(request.job_order || "")}</div>
            ${
              request?.job_order && getShipmentConsignee(request)
                ? `<div><strong>Shipment Consignee:</strong> ${escapeHtml(getShipmentConsignee(request))}</div>`
                : ""
            }
            <div><strong>Corporate Client:</strong> ${escapeHtml(request.corporate_client || "")}</div>
            ${soa.forSoa ? `<div><strong>For SOA:</strong> Yes</div><div><strong>SOA Remarks:</strong> ${escapeHtml(soa.remarks || "—")}</div>` : ""}
            <div><strong>Shipment Description:</strong> ${escapeHtml(request.shipment_description || "")}</div>
          </div>
          ${
            requestRemarks
              ? `<div style="margin-bottom:10px;font-size:13px;"><strong>Remarks:</strong> ${escapeHtml(
                  requestRemarks
                )}</div>`
              : ""
          }

          <table>
            <thead>
              <tr>
                <th style="width:40%;text-align:left;border:1px solid #000;padding:8px 10px;">
                  PARTICULARS<br><small>Reference / Description / Quantity / Unit cost</small>
                </th>
                <th style="width:12%;text-align:right;border:1px solid #000;padding:8px 10px;">NET AMOUNT</th>
                <th style="width:12%;text-align:right;border:1px solid #000;padding:8px 10px;">TAX</th>
                <th style="width:12%;text-align:right;border:1px solid #000;padding:8px 10px;">EWT</th>
                <th style="width:14%;text-align:right;border:1px solid #000;padding:8px 10px;">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              ${particularsRowsHtml}
              <tr>
                <td style="height:70px;border:1px solid #000;"></td>
                <td style="border:1px solid #000;"></td>
                <td style="border:1px solid #000;"></td>
                <td style="border:1px solid #000;"></td>
                <td style="border:1px solid #000;"></td>
              </tr>
            </tbody>
          </table>

          <div class="request-total-wrap">
            <div class="request-total-box">
              <strong>TOTAL ${itemCurrency}</strong>
              ${
                isUsdItem
                  ? `<div style="display:inline-flex;flex-direction:column;align-items:flex-end;">
                      <span>$${money(requestTotalOriginal)}</span>
                      <span style="font-size:11px;color:#555;">₱${money(requestTotalPhp)}</span>
                    </div>`
                  : `₱${money(requestTotalPhp)}`
              }
            </div>
            ${
              requestTotalEwtPhp > 0
                ? `
            <div class="request-total-box">
              <strong>LESS: EWT</strong>
              ${
                isUsdItem
                  ? `<div style="display:inline-flex;flex-direction:column;align-items:flex-end;">
                      <span>($${money(requestTotalEwtOriginal)})</span>
                      <span style="font-size:11px;color:#555;">(₱${money(requestTotalEwtPhp)})</span>
                    </div>`
                  : `(₱${money(requestTotalEwtPhp)})`
              }
            </div>
            <div class="request-total-box">
              <strong>TOTAL AMOUNT DUE</strong>
              ${
                isUsdItem
                  ? `<div style="display:inline-flex;flex-direction:column;align-items:flex-end;">
                      <span>$${money(requestNetPayableOriginal)}</span>
                      <span style="font-size:11px;color:#555;">₱${money(requestNetPayablePhp)}</span>
                    </div>`
                  : `₱${money(requestNetPayablePhp)}`
              }
            </div>
            `
                : ""
            }
          </div>

          <div style="margin-top:10px;">
            <div><strong>Received from:</strong> _______________________</div>
            <div><strong>The amount of PESOS:</strong> __________________</div>
            <div class="request-note">In full payment of amount described above.</div>
          </div>

          <table class="request-signature-grid" style="width:100%;margin-top:24px;border:none;">
            <tr>
              <td style="text-align:center;vertical-align:bottom;border:none;padding:0 10px;height:60px;">
                ${preparerSignatureUrl ? `<img src="${preparerSignatureUrl}" style="max-height:50px;max-width:120px;">` : ""}
              </td>
              <td style="text-align:center;vertical-align:bottom;border:none;padding:0 10px;height:60px;">
                ${approverSignatureUrl ? `<img src="${approverSignatureUrl}" style="max-height:50px;max-width:120px;">` : ""}
              </td>
              <td style="text-align:center;vertical-align:bottom;border:none;padding:0 10px;height:60px;">
                ${receiverSignatureUrl ? `<img src="${receiverSignatureUrl}" style="max-height:50px;max-width:120px;">` : ""}
              </td>
            </tr>
            <tr>
              <td style="text-align:center;border:none;padding:0 10px;">
                <div style="border-top:1px solid #000;margin:8px 0;"></div>
                Prepared by: ${escapeHtml(request.preparer_name || "____________________")}
              </td>
              <td style="text-align:center;border:none;padding:0 10px;">
                <div style="border-top:1px solid #000;margin:8px 0;"></div>
                Approved by (Final): ${escapeHtml(request.approver_name || "____________________")}
              </td>
              <td style="text-align:center;border:none;padding:0 10px;">
                <div style="border-top:1px solid #000;margin:8px 0;"></div>
                Approved/Received by: ${escapeHtml(request.receiver_name || "____________________")}
              </td>
            </tr>
          </table>
        </section>
      `;
    })
    .join("");

const buildVoucherOnlyHtml = ({ voucher, primaryRequest, lines = [] }) => {
  const preparerSignatureUrl = resolveSignatureUrl(voucher?.preparer_signature_url);
  const checkedBySignatureUrl = resolveSignatureUrl(voucher?.checked_by_signature_url);
  const shipmentConsignee =
    primaryRequest?.shipment_consignee || primaryRequest?.receiver_name || "";

  const lineRowsHtml = lines
    .map(
      (line) => `<tr>
        <td style="padding:8px 10px;border:1px solid #000;">
          <div>${line.account_name || ""}</div>
          ${line.particulars ? `<div style="font-size:11px;color:#444;margin-top:2px;">${line.particulars}</div>` : ""}
        </td>
        <td style="padding:8px 10px;border:1px solid #000;text-align:right;">${Number(line.debit || 0) ? money(line.debit) : ""}</td>
        <td style="padding:8px 10px;border:1px solid #000;text-align:right;">${Number(line.credit || 0) ? money(line.credit) : ""}</td>
      </tr>`
    )
    .join("");

  return `
    <html>
      <head>
        <title>${voucher?.voucher_no || "Check Voucher"}</title>
        <style>
          *{box-sizing:border-box;}
          body{font-family:"Times New Roman",serif;color:#000;margin:18px;}
          .brand{display:flex;justify-content:center;align-items:center;margin-bottom:8px;}
          .brand img{max-width:180px;max-height:70px;object-fit:contain;}
          h1{text-align:center;letter-spacing:2px;margin:0 0 4px;}
          .subhead{text-align:center;font-weight:600;margin:0 0 12px;}
          .meta{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px 16px;margin-bottom:12px;}
          table{width:100%;border-collapse:collapse;margin-top:8px;}
          th,td{vertical-align:top;}
          .sig-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-top:16px;}
          .sig{text-align:center;min-height:74px;display:flex;flex-direction:column;justify-content:flex-end;gap:4px;}
          .sig img{max-height:28px;object-fit:contain;margin:0 auto;}
          .sig-label{font-weight:700;}
          .sig-name{font-size:12px;min-height:14px;}
          .line{border-top:1px solid #000;height:1px;margin:6px 0;}
          @page{size:portrait;margin:12mm;}
        </style>
      </head>
      <body>
        <div class="brand">
          <img src="${rcsLogo}" alt="RCS Logistics Philippines logo">
        </div>
        <h1>CHECK VOUCHER</h1>
        <div class="subhead">${voucher?.voucher_no || ""}</div>
        <div class="meta">
          <div><strong>Date:</strong> ${voucher?.voucher_date || ""}</div>
          <div><strong>Payee:</strong> ${voucher?.payee || ""}</div>
          <div><strong>Check No.:</strong> ${voucher?.check_no || ""}</div>
          <div><strong>Check Date:</strong> ${voucher?.check_date || ""}</div>
          ${voucher?.for_soa ? `<div><strong>For SOA:</strong> Yes</div><div><strong>SOA Remarks:</strong> ${escapeHtml(voucher?.soa_remarks || "—")}</div>` : ""}
          <div><strong>Due Date:</strong> ${formatDate(primaryRequest?.due_date) || "—"}</div>
          <div><strong>Mode of Payment:</strong> ${escapeHtml(primaryRequest?.mode_of_payment || "—")}</div>
          ${
            primaryRequest?.job_order && shipmentConsignee
              ? `<div><strong>Shipment Consignee:</strong> ${escapeHtml(shipmentConsignee)}</div>`
              : ""
          }
          <div><strong>Amount:</strong> ₱${money(voucher?.amount || 0)}</div>
          <div><strong>Amount in Words:</strong> ${voucher?.amount_words || ""}</div>
        </div>

        <div style="margin:14px 0;font-size:13px;">
          <div><strong>Prepared by:</strong> ${voucher?.preparer_name || "____________________"}</div>
          <div><strong>Checked by:</strong> ${voucher?.checked_by_name || "____________________"}</div>
          <div><strong>Approved by:</strong> ${voucher?.approved_by_name || "____________________"}</div>
          <div><strong>Received by:</strong> ${voucher?.received_by_name || "____________________"}</div>
        </div>

        <div style="font-weight:700;margin-bottom:6px;">Accounting Entries</div>
        <table>
          <thead>
            <tr>
              <th style="width:60%;text-align:left;border:1px solid #000;padding:8px 10px;">Account</th>
              <th style="width:20%;text-align:right;border:1px solid #000;padding:8px 10px;">Debit</th>
              <th style="width:20%;text-align:right;border:1px solid #000;padding:8px 10px;">Credit</th>
            </tr>
          </thead>
          <tbody>
            ${lineRowsHtml}
            <tr>
              <td style="padding:8px 10px;border:1px solid #000;font-weight:700;text-align:right;">TOTAL</td>
              <td style="padding:8px 10px;border:1px solid #000;text-align:right;font-weight:700;">₱${money(voucher?.amount || 0)}</td>
              <td style="padding:8px 10px;border:1px solid #000;text-align:right;font-weight:700;">₱${money(voucher?.amount || 0)}</td>
            </tr>
          </tbody>
        </table>

        <div class="sig-grid">
          <div class="sig">
            ${preparerSignatureUrl ? `<img src="${preparerSignatureUrl}" alt="Preparer signature">` : "<div style='height:20px'></div>"}
            <div class="line"></div>
            <div class="sig-name">${voucher?.preparer_name || "Name"}</div>
            <div class="sig-label">Prepared By:</div>
          </div>
          <div class="sig">
            ${checkedBySignatureUrl ? `<img src="${checkedBySignatureUrl}" alt="Checked by signature">` : "<div style='height:20px'></div>"}
            <div class="line"></div>
            <div class="sig-name">${voucher?.checked_by_name || "Name"}</div>
            <div class="sig-label">Checked By:</div>
          </div>
          <div class="sig">
            <div style='height:20px'></div>
            <div class="line"></div>
            <div class="sig-name">${voucher?.approved_by_name || "Name"}</div>
            <div class="sig-label">Approved By:</div>
          </div>
          <div class="sig">
            <div style='height:20px'></div>
            <div class="line"></div>
            <div class="sig-name">${voucher?.received_by_name || "Name"}</div>
            <div class="sig-label">Received By:</div>
          </div>
        </div>
      </body>
    </html>
  `;
};

export const buildCheckVoucherRequestHtml = ({ requests = [] }) => `
  <html>
    <head>
      <title>Request Details</title>
      <style>
        *{box-sizing:border-box;}
        body{font-family:"Times New Roman",serif;color:#000;margin:18px;}
        .request-page{margin-top:18px;}
        .request-page-break{page-break-before:always;}
        .request-name{text-align:center;font-size:28px;font-weight:700;letter-spacing:2px;margin:0 0 4px;}
        .request-subhead{text-align:center;font-size:14px;font-weight:700;margin-bottom:14px;}
        .request-total-wrap{margin-top:8px;display:flex;justify-content:flex-end;}
        .request-total-box{border:1px solid #000;padding:8px 12px;min-width:240px;text-align:right;}
        .request-note{font-size:12px;margin-top:4px;}
        .request-signature-grid{border:none;}
        table{width:100%;border-collapse:collapse;margin-top:8px;}
        th,td{vertical-align:top;}
        @page{size:portrait;margin:12mm;}
      </style>
    </head>
    <body>
      ${buildRequestDetailHtml(requests)}
    </body>
  </html>
`;

export const buildCheckVoucherPrintHtml = ({ voucher, requests = [], lines = [] }) => {
  const preparerSignatureUrl = resolveSignatureUrl(voucher?.preparer_signature_url);
  const checkedBySignatureUrl = resolveSignatureUrl(voucher?.checked_by_signature_url);
  const primaryRequest = Array.isArray(requests) && requests.length ? requests[0] : null;
  const shipmentConsignee =
    primaryRequest?.shipment_consignee || primaryRequest?.receiver_name || "";
  const requestDetailHtml = buildRequestDetailHtml(requests, {
    forSoa: voucher?.for_soa,
    remarks: voucher?.soa_remarks,
  });
  const requestRowsHtml = requests
    .map(
      (row, index) => `<tr>
        <td style="padding:8px 10px;border:1px solid #000;">${index + 1}</td>
        <td style="padding:8px 10px;border:1px solid #000;">${row.rfp_no || "—"}</td>
        <td style="padding:8px 10px;border:1px solid #000;">${row.job_order || "—"}</td>
        <td style="padding:8px 10px;border:1px solid #000;">${row.payee || "—"}</td>
        <td style="padding:8px 10px;border:1px solid #000;text-align:right;">₱${money(row.total_amount || 0)}</td>
        <td style="padding:8px 10px;border:1px solid #000;">${formatRequestParticulars(row) || "—"}</td>
      </tr>`
    )
    .join("");

  const lineRowsHtml = lines
    .map(
      (line) => `<tr>
        <td style="padding:8px 10px;border:1px solid #000;">
          <div>${line.account_name || ""}</div>
          ${line.particulars ? `<div style="font-size:11px;color:#444;margin-top:2px;">${line.particulars}</div>` : ""}
        </td>
        <td style="padding:8px 10px;border:1px solid #000;text-align:right;">${Number(line.debit || 0) ? money(line.debit) : ""}</td>
        <td style="padding:8px 10px;border:1px solid #000;text-align:right;">${Number(line.credit || 0) ? money(line.credit) : ""}</td>
      </tr>`
    )
    .join("");

  return `
    <html>
      <head>
        <title>${voucher?.voucher_no || "Check Voucher"}</title>
        <style>
          *{box-sizing:border-box;}
          body{font-family:"Times New Roman",serif;color:#000;margin:18px;}
          .brand{display:flex;justify-content:center;align-items:center;margin-bottom:8px;}
          .brand img{max-width:180px;max-height:70px;object-fit:contain;}
          h1{text-align:center;letter-spacing:2px;margin:0 0 4px;}
          .subhead{text-align:center;font-weight:600;margin:0 0 12px;}
          .meta{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px 16px;margin-bottom:12px;}
          .grid{display:block;}
          .block{padding:0;width:100%;}
          table{width:100%;border-collapse:collapse;margin-top:8px;}
          th,td{vertical-align:top;}
          .sig-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-top:16px;}
          .sig{text-align:center;min-height:74px;display:flex;flex-direction:column;justify-content:flex-end;gap:4px;}
          .sig img{max-height:28px;object-fit:contain;margin:0 auto;}
          .sig-label{font-weight:700;}
          .sig-name{font-size:12px;min-height:14px;}
          .line{border-top:1px solid #000;height:1px;margin:6px 0;}
          .request-page{margin-top:18px;}
          .request-page-break{page-break-before:always;}
          .request-title{text-align:center;font-size:16px;font-weight:700;letter-spacing:0.14em;margin-bottom:8px;}
          .request-name{text-align:center;font-size:28px;font-weight:700;letter-spacing:0.16em;margin-bottom:4px;}
          .request-subhead{text-align:center;font-size:14px;font-weight:700;margin-bottom:14px;}
          .request-meta{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px 18px;margin-bottom:12px;font-size:13px;}
          .request-meta-full{grid-column:1 / -1;}
          .request-total-wrap{margin-top:8px;display:flex;justify-content:flex-end;}
          .request-total-box{border:1px solid #000;padding:8px 12px;min-width:240px;text-align:right;}
          .attachment-page{page-break-before:always;break-before:page;height:calc(100vh - 24mm);display:flex;align-items:stretch;justify-content:stretch;}
          .attachment-document{flex:1;display:flex;align-items:stretch;justify-content:stretch;height:100%;}
          .attachment-image{width:100%;display:block;}
          .attachment-image-full{height:100%;object-fit:contain;}
          .attachment-frame{width:100%;border:0;display:block;}
          .attachment-frame-full{height:100%;}
          .attachment-fallback{padding:24px;text-align:center;font-size:13px;color:#333;}
          .attachment-fallback-full{width:100%;border:1px dashed #777;display:flex;flex-direction:column;align-items:center;justify-content:center;}
          .attachment-fallback-name{margin-top:8px;font-weight:700;word-break:break-word;}
          @page{size:portrait;margin:12mm;}
        </style>
      </head>
      <body>
        <div class="brand">
          <img src="${rcsLogo}" alt="RCS Logistics Philippines logo">
        </div>
        <h1>CHECK VOUCHER</h1>
        <div class="subhead">${voucher?.voucher_no || ""}</div>
        <div class="meta">
          <div><strong>Date:</strong> ${voucher?.voucher_date || ""}</div>
          <div><strong>Payee:</strong> ${voucher?.payee || ""}</div>
          <div><strong>Check No.:</strong> ${voucher?.check_no || ""}</div>
          <div><strong>Check Date:</strong> ${voucher?.check_date || ""}</div>
          ${voucher?.for_soa ? `<div><strong>For SOA:</strong> Yes</div><div><strong>SOA Remarks:</strong> ${escapeHtml(voucher?.soa_remarks || "—")}</div>` : ""}
          <div><strong>Due Date:</strong> ${formatDate(primaryRequest?.due_date) || "—"}</div>
          <div><strong>Mode of Payment:</strong> ${escapeHtml(primaryRequest?.mode_of_payment || "—")}</div>
          ${
            primaryRequest?.job_order && shipmentConsignee
              ? `<div><strong>Shipment Consignee:</strong> ${escapeHtml(shipmentConsignee)}</div>`
              : ""
          }
          <div><strong>Amount:</strong> ₱${money(voucher?.amount || 0)}</div>
          <div><strong>Amount in Words:</strong> ${voucher?.amount_words || ""}</div>
        </div>

        <div style="margin:10px 0 12px;font-size:13px;">
          <div style="font-weight:700;margin-bottom:6px;">The above check is in payment of the following</div>
          <table style="width:100%;border-collapse:collapse;">
            <thead>
              <tr>
                <th style="text-align:left;border:1px solid #000;padding:8px 10px;">#</th>
                <th style="text-align:left;border:1px solid #000;padding:8px 10px;">RFP No</th>
                <th style="text-align:left;border:1px solid #000;padding:8px 10px;">Job Order</th>
                <th style="text-align:left;border:1px solid #000;padding:8px 10px;">Payee</th>
                <th style="text-align:right;border:1px solid #000;padding:8px 10px;">Amount</th>
                <th style="text-align:left;border:1px solid #000;padding:8px 10px;">Particulars</th>
              </tr>
            </thead>
            <tbody>${requestRowsHtml}</tbody>
          </table>
        </div>

        ${(() => {
          const requestsWithRemarks = requests.filter((r) => r.remarks && r.remarks.trim());
          if (!requestsWithRemarks.length) return "";
          return `
            <div style="margin:10px 0 12px;font-size:13px;">
              <div style="font-weight:700;margin-bottom:6px;">Request Remarks</div>
              ${requestsWithRemarks
                .map(
                  (r) => `<div style="margin-bottom:4px;"><strong>${r.rfp_no || "—"}:</strong> ${r.remarks || "—"}</div>`
                )
                .join("")}
            </div>
          `;
        })()}

        <div class="grid">
          <div class="block">
            <div style="font-weight:700;margin-bottom:6px;">Accounting Entries</div>
            <table>
              <thead>
                <tr>
                  <th style="width:60%;text-align:left;border:1px solid #000;padding:8px 10px;">Account</th>
                  <th style="width:20%;text-align:right;border:1px solid #000;padding:8px 10px;">Debit</th>
                  <th style="width:20%;text-align:right;border:1px solid #000;padding:8px 10px;">Credit</th>
                </tr>
              </thead>
              <tbody>
                ${lineRowsHtml}
                <tr>
                  <td style="padding:8px 10px;border:1px solid #000;font-weight:700;text-align:right;">TOTAL</td>
                  <td style="padding:8px 10px;border:1px solid #000;text-align:right;font-weight:700;">₱${money(voucher?.amount || 0)}</td>
                  <td style="padding:8px 10px;border:1px solid #000;text-align:right;font-weight:700;">₱${money(voucher?.amount || 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div style="margin-top:14px;font-size:13px;">
          <div><strong>Prepared by:</strong> ${voucher?.preparer_name || "____________________"}</div>
          <div><strong>Checked by:</strong> ${voucher?.checked_by_name || "____________________"}</div>
          <div><strong>Approved by:</strong> ${voucher?.approved_by_name || "____________________"}</div>
          <div><strong>Received by:</strong> ${voucher?.received_by_name || "____________________"}</div>
        </div>

        <div class="sig-grid">
          <div class="sig">
            ${preparerSignatureUrl ? `<img src="${preparerSignatureUrl}" alt="Preparer signature">` : "<div style='height:20px'></div>"}
            <div class="line"></div>
            <div class="sig-name">${voucher?.preparer_name || "Name"}</div>
            <div class="sig-label">Prepared By:</div>
          </div>
          <div class="sig">
            ${checkedBySignatureUrl ? `<img src="${checkedBySignatureUrl}" alt="Checked by signature">` : "<div style='height:20px'></div>"}
            <div class="line"></div>
            <div class="sig-name">${voucher?.checked_by_name || "Name"}</div>
            <div class="sig-label">Checked By:</div>
          </div>
          <div class="sig">
            <div style='height:20px'></div>
            <div class="line"></div>
            <div class="sig-name">${voucher?.approved_by_name || "Name"}</div>
            <div class="sig-label">Approved By:</div>
          </div>
          <div class="sig">
            <div style='height:20px'></div>
            <div class="line"></div>
            <div class="sig-name">${voucher?.received_by_name || "Name"}</div>
            <div class="sig-label">Received By:</div>
          </div>
        </div>

        ${requestDetailHtml}
      </body>
    </html>
  `;
};

export const buildCheckVoucherPacketHtml = ({ voucher, requests = [], lines = [] }) =>
  `${buildVoucherOnlyHtml({
    voucher,
    primaryRequest: Array.isArray(requests) && requests.length ? requests[0] : null,
    lines,
  }).replace("</body></html>", "")}
  ${buildRequestDetailHtml(requests)}
  </body>
  </html>`;
